import { tryToParseInt } from "@/components/utils/tryToParseInt";
import { useMultiFieldSet } from "@/components/utils/useFieldSet";
import { NonNullish } from "@/flokit/utils/comparison/non-nullish";
import { isNil } from "lodash";
import {
  JobAppSectionResponseSectionType,
  JobAppSelfIdQuestionAnswerRequest,
  JobAppSelfIdQuestionAnswerSelectionRequest,
} from "modules/api-docs";
import {
  PublicOrgSelfIdQuestionsQuery,
  SelfIdType,
} from "modules/graphql.types";
import { candidateSelfIdQuestionAnswerYup } from "modules/self-apply/validation/candidate-self-apply";
import { useMemo } from "react";
import {
  SelfApplyOnlyNullish,
  SelfApplyOnlyNullishExistingJobApp,
  SelfApplyOnlyNullishJobSection,
  selfApplyOnlyBaseIsSectionRequired,
} from "./common";

type ExistingSelfIdQuestionAnswer = NonNullable<
  NonNullable<SelfApplyOnlyNullishExistingJobApp>["selfIdAnswers"]
>[number] &
  SelfApplyOnlyNullish<{ text?: SelfApplyOnlyNullish<string> }>;

export function isSelfIdRequired(
  sections: SelfApplyOnlyNullishJobSection[],
  isGovtContractor: SelfApplyOnlyNullish<boolean>,
) {
  if (isGovtContractor) {
    return false;
  }

  return selfApplyOnlyBaseIsSectionRequired(
    sections,
    JobAppSectionResponseSectionType.SELF_IDENTIFICATION,
  );
}

function getExistingAnswerBySelfIdQuestionId(
  existingAnswers: ExistingSelfIdQuestionAnswer[] | null,
) {
  const existingAnswerIdByQuestionId = new Map<
    number,
    ExistingSelfIdQuestionAnswer
  >();
  if (!existingAnswers) {
    return new Map<number, ExistingSelfIdQuestionAnswer>();
  }

  for (const existingAnswer of existingAnswers) {
    if (!existingAnswer) {
      continue;
    }
    const questionId = existingAnswer.orgSelfIdQuestionId;
    if (isNil(questionId)) {
      continue;
    }

    existingAnswerIdByQuestionId.set(questionId, existingAnswer);
  }

  return existingAnswerIdByQuestionId;
}

export function extractSelfIdQuestionAnswersFromExistingJobApp(args: {
  existingAnswers: ExistingSelfIdQuestionAnswer[];
  selfIdQuestions: PublicOrgSelfIdQuestionsQuery["publicOrgSelfIdQuestions"];
}) {
  const existingAnswers: JobAppSelfIdQuestionAnswerRequest[] = [];
  if (!args.selfIdQuestions) {
    return existingAnswers;
  }

  const selfIdQuestions = args.selfIdQuestions
    .filter(NonNullish)
    .filter((orgIdQuestion) => {
      return (
        orgIdQuestion?.enabled &&
        orgIdQuestion.selfIdType !== SelfIdType.Description
      );
    });

  const existingAnswerByQuestionId = getExistingAnswerBySelfIdQuestionId(
    args.existingAnswers,
  );

  for (const selfIdQuestion of selfIdQuestions) {
    const questionId = tryToParseInt(selfIdQuestion.id).extract();
    if (!questionId) {
      continue;
    }

    const existingAnswer = existingAnswerByQuestionId.get(questionId);
    const existingAnswerId = tryToParseInt(existingAnswer?.id);
    // Populate with empty answer for unanswered questions
    if (
      // Question not answered
      !existingAnswer ||
      // Something went wrong; question was already answered but no answer id from the backend
      !existingAnswerId.isJust()
    ) {
      existingAnswers.push({
        id: questionId,
        jobAppSelfIdQuestionAnswerSelections: [],
        orgSelfIdQuestionId: questionId,
        text: "",
      });

      continue;
    }

    const answerRequest: JobAppSelfIdQuestionAnswerRequest = {
      id: questionId,
      jobAppSelfIdQuestionAnswerSelections: (
        existingAnswer?.jobAppSelfIdQuestionAnswerSelections ?? []
      )
        .filter((selection) => !isNil(selection))
        .map((selection) => {
          const selectionReq: JobAppSelfIdQuestionAnswerSelectionRequest = {
            id: tryToParseInt(selection?.id).extract(),
            orgSelfIdQuestionOptionId:
              selection?.orgSelfIdQuestionOptionId ?? undefined,
          };

          return selectionReq;
        }),
      orgSelfIdQuestionId: questionId,
      text: existingAnswer?.text ?? "",
    };

    existingAnswers.push(answerRequest);
  }

  return existingAnswers;
}

type UseSelfIdQuestionsFieldSetArgs = {
  jobSections: SelfApplyOnlyNullishJobSection[];
  existingJobApp: SelfApplyOnlyNullishExistingJobApp;
  selfIdQuestions: PublicOrgSelfIdQuestionsQuery["publicOrgSelfIdQuestions"];
  isGovtContractor: SelfApplyOnlyNullish<boolean>;
};
export function useSelfIdQuestionAnswersFieldSet({
  jobSections,
  existingJobApp,
  selfIdQuestions,
  isGovtContractor,
}: UseSelfIdQuestionsFieldSetArgs) {
  const existingAnswers = existingJobApp?.selfIdAnswers;

  const initialValue = useMemo(() => {
    return extractSelfIdQuestionAnswersFromExistingJobApp({
      existingAnswers: existingAnswers ?? [],
      selfIdQuestions,
    });
  }, [selfIdQuestions, existingAnswers, jobSections]);

  return useMultiFieldSet<JobAppSelfIdQuestionAnswerRequest>(
    initialValue,
    [initialValue],
    isSelfIdRequired(jobSections, isGovtContractor)
      ? candidateSelfIdQuestionAnswerYup
      : undefined,
  );
}

/**
 * When updating an existing question/answer, sets the answer id in the request.
 */
export function selfIdQuestionAnswersToRequests(args: {
  answers: JobAppSelfIdQuestionAnswerRequest[];
  existingAnswers: ExistingSelfIdQuestionAnswer[];
}): JobAppSelfIdQuestionAnswerRequest[] {
  // All new, so ids to set
  if (!args.existingAnswers || args.existingAnswers.length === 0) {
    return args.answers;
  }

  const existingAnswerByQuestionId = getExistingAnswerBySelfIdQuestionId(
    args.existingAnswers,
  );

  return args.answers
    .map((answer) => {
      const questionId = answer.orgSelfIdQuestionId;
      if (isNil(questionId)) {
        return null;
      }

      const request: JobAppSelfIdQuestionAnswerRequest = {
        id: tryToParseInt(
          existingAnswerByQuestionId.get(questionId)?.id,
        ).extract(),
        jobAppSelfIdQuestionAnswerSelections:
          answer.jobAppSelfIdQuestionAnswerSelections,
        orgSelfIdQuestionId: questionId,
        text: answer.text,
      };

      return request;
    })
    .filter(NonNullish);
}
